/**
 * V7-A (14.06.2026) — Intermarket-Analyse: die „große Sicht" zueinander.
 *
 * Trägt die Makro-Treiber (Gold/Silber/Öl/Dollar/Zinsen/Kupfer/High-Yield)
 * zu EINER Lage zusammen und übersetzt sie auf den einzelnen Wert — der
 * Profi-Überblick, für den dem Nebenberufler abends die Zeit fehlt.
 *
 * LEITPLANKE (User): besserer KONTEXT, keine Glaskugel. Liefert
 * Rückenwind/Gegenwind + Wahrscheinlichkeits-Lage (Lehrbuch-Intermarket
 * nach J. Murphy), KEINE verlässliche Prognose. Regelbasiert, kein ML.
 *
 * Reine Funktionen, voll testbar. Der Aufrufer bestimmt den Trend je
 * Treiber aus den OHLCV-Bars (Kurs vs. MA50/MA200, 13W-Return) und reicht
 * ihn hier rein — die Lib selbst lädt nichts.
 */

export type Trend = 'up' | 'down' | 'flat';

/** Die verfolgten Makro-Treiber (ETF-Proxies in der OHLCV-Pipeline). */
export type DriverKey =
  | 'gold' // GLD
  | 'silver' // SLV
  | 'oil' // USO/CL
  | 'dollar' // UUP
  | 'rates' // 10Y-Zins / TLT invers
  | 'copper' // CPER — „Dr. Copper", Konjunktur
  | 'highyield' // HYG — Risikoappetit
  | 'stocks'; // SPY

export interface DriverState {
  trend: Trend;
  /** 13-Wochen-Return in %, optional (für die Stärke der Aussage). */
  change13w?: number | null;
}

export type IntermarketInput = Partial<Record<DriverKey, DriverState | null>>;

export type IntermarketTone = 'risk_on' | 'risk_off' | 'inflation' | 'mixed' | 'unknown';

export interface DriverLine {
  key: DriverKey;
  label: string;
  trend: Trend;
  /** Ein Halbsatz, was dieser Treiber gerade „sagt". */
  note: string;
}

export interface IntermarketRegime {
  tone: IntermarketTone;
  /** Kurzes Lage-Label, z.B. „Risk-on mit Rohstoff-Stärke". */
  label: string;
  /** Ein Satz Klartext für die Markt-Leiste. */
  satz: string;
  /** Je verfügbarem Treiber eine Zeile (fehlende werden ausgelassen). */
  drivers: DriverLine[];
}

const DRIVER_LABEL: Record<DriverKey, string> = {
  gold: 'Gold',
  silver: 'Silber',
  oil: 'Öl',
  dollar: 'Dollar',
  rates: 'Zinsen (10J)',
  copper: 'Kupfer',
  highyield: 'High-Yield',
  stocks: 'Aktien (S&P)',
};

function noteFor(key: DriverKey, t: Trend): string {
  const up = t === 'up';
  const flat = t === 'flat';
  switch (key) {
    case 'gold':
      return flat ? 'seitwärts' : up ? 'steigt — Absicherung/Angst gefragt' : 'fällt — weniger Absicherungsbedarf';
    case 'silver':
      return flat ? 'seitwärts' : up ? 'steigt — Industrie-/Edelmetall-Nachfrage' : 'fällt';
    case 'oil':
      return flat ? 'seitwärts' : up ? 'steigt — Energie teurer, Input-Kosten hoch' : 'fällt — Entlastung für Industrie/Konsum';
    case 'dollar':
      return flat ? 'seitwärts' : up ? 'stark — Gegenwind für Rohstoffe & Exporteure' : 'schwach — Rückenwind für Rohstoffe & Exporteure';
    case 'rates':
      return flat ? 'seitwärts' : up ? 'steigen — Druck auf Zins-Sensible, Rückenwind Banken' : 'fallen — Entlastung für Zins-Sensible';
    case 'copper':
      return flat ? 'seitwärts' : up ? 'steigt — Konjunktur-Optimismus' : 'fällt — Konjunktur-Skepsis';
    case 'highyield':
      return flat ? 'seitwärts' : up ? 'steigt — Risikoappetit da' : 'fällt — Risiko wird gemieden';
    case 'stocks':
      return flat ? 'seitwärts' : up ? 'im Aufwärtstrend' : 'unter Druck';
  }
}

function t(input: IntermarketInput, key: DriverKey): Trend | null {
  const s = input[key];
  return s ? s.trend : null;
}

/**
 * Trägt die Treiber zur Gesamtlage zusammen. Fehlende Treiber werden
 * ausgelassen (kein Raten) — das Urteil wird mit dem, was da ist, gefällt.
 */
export function computeIntermarketRegime(input: IntermarketInput): IntermarketRegime {
  const drivers: DriverLine[] = [];
  for (const key of Object.keys(DRIVER_LABEL) as DriverKey[]) {
    const s = input[key];
    if (!s) continue;
    drivers.push({ key, label: DRIVER_LABEL[key], trend: s.trend, note: noteFor(key, s.trend) });
  }

  const gold = t(input, 'gold');
  const rates = t(input, 'rates');
  const stocks = t(input, 'stocks');
  const copper = t(input, 'copper');
  const hy = t(input, 'highyield');
  const dollar = t(input, 'dollar');

  // Lage-Heuristik (Murphy, bewusst grob & nachvollziehbar)
  let tone: IntermarketTone = 'unknown';
  let label = 'Lage unklar';

  const riskOnVotes = [stocks === 'up', copper === 'up', hy === 'up'].filter(Boolean).length;
  const riskOffVotes = [gold === 'up' && rates === 'down', hy === 'down', stocks === 'down'].filter(
    Boolean
  ).length;

  if (gold === 'up' && rates === 'up') {
    tone = 'inflation';
    label = 'Inflationssorge';
  } else if (riskOffVotes >= 2 || (gold === 'up' && rates === 'down' && hy !== 'up')) {
    tone = 'risk_off';
    label = 'Risk-off / Flucht in Sicherheit';
  } else if (riskOnVotes >= 2) {
    tone =
      copper === 'up' || t(input, 'oil') === 'up' ? 'risk_on' : 'risk_on';
    label = copper === 'up' ? 'Risk-on mit Rohstoff-Stärke' : 'Risk-on';
  } else if (drivers.length > 0) {
    tone = 'mixed';
    label = 'Gemischtes Bild';
  }

  const dollarHint =
    dollar === 'up'
      ? ' Der starke Dollar bremst Rohstoffe und US-Exporteure.'
      : dollar === 'down'
        ? ' Der schwache Dollar stützt Rohstoffe und Exporteure.'
        : '';

  const satz =
    tone === 'unknown'
      ? 'Noch zu wenig Makro-Daten für eine Gesamtlage.'
      : `${label}.${dollarHint}`.trim();

  return { tone, label, satz, drivers };
}

// ---------------------------------------------------------------------------
// Übersetzung auf den einzelnen Wert
// ---------------------------------------------------------------------------

export interface AssetMacroInput {
  assetClass: string | null; // 'stock'|'etf'|'crypto'|'commodity_etf'|...
  assetSubtype: string | null; // quality|dividend|momentum|tesla|gold|sector_etf|...
  /** Optionaler GICS-/Sektor-Hint (z.B. „Energy", „Financials", „Real Estate"). */
  sector?: string | null;
}

export interface AssetMacroVerdict {
  tone: 'tailwind' | 'headwind' | 'neutral';
  /** Ein Satz: Makro-Rückenwind/-Gegenwind für DIESEN Wert. */
  satz: string;
}

function sectorMatches(sector: string | null | undefined, ...needles: string[]): boolean {
  if (!sector) return false;
  const s = sector.toLowerCase();
  return needles.some((n) => s.includes(n));
}

/**
 * Welcher Makro-Treiber wirkt auf diesen Wert — und wie steht er gerade?
 * Konservativ: nur klare Lehrbuch-Beziehungen, sonst neutral.
 */
export function intermarketForAsset(
  regime: IntermarketRegime,
  asset: AssetMacroInput,
  input: IntermarketInput
): AssetMacroVerdict {
  const sub = asset.assetSubtype;
  const cls = asset.assetClass;
  const sec = asset.sector;
  const dollar = t(input, 'dollar');
  const oil = t(input, 'oil');
  const rates = t(input, 'rates');
  const gold = t(input, 'gold');

  // Gold / Edelmetalle
  if (sub === 'gold' || sectorMatches(sec, 'gold', 'metal', 'mining')) {
    if (gold === 'up' && dollar !== 'up')
      return { tone: 'tailwind', satz: 'Makro-Rückenwind: Gold steigt, Dollar nicht stark — gutes Umfeld für Edelmetall-Werte.' };
    if (gold === 'down' || dollar === 'up')
      return { tone: 'headwind', satz: 'Makro-Gegenwind: schwächeres Gold bzw. starker Dollar belasten Edelmetall-Werte.' };
    return { tone: 'neutral', satz: 'Edelmetall-Umfeld neutral.' };
  }

  // Krypto
  if (cls === 'crypto' || sub === 'crypto_anchor') {
    if (regime.tone === 'risk_on' && dollar !== 'up')
      return { tone: 'tailwind', satz: 'Makro-Rückenwind: Risk-on-Lage und kein starker Dollar — günstig für Krypto.' };
    if (regime.tone === 'risk_off' || dollar === 'up')
      return { tone: 'headwind', satz: 'Makro-Gegenwind: Risk-off bzw. starker Dollar — Liquidität flieht aus Krypto.' };
    return { tone: 'neutral', satz: 'Krypto-Makro-Umfeld neutral.' };
  }

  // Energie
  if (sectorMatches(sec, 'energy', 'oil', 'gas')) {
    if (oil === 'up') return { tone: 'tailwind', satz: 'Makro-Rückenwind: steigendes Öl stützt Energie-Werte.' };
    if (oil === 'down') return { tone: 'headwind', satz: 'Makro-Gegenwind: fallendes Öl belastet Energie-Werte.' };
  }

  // Zins-Sensible: REITs, Versorger, Langläufer
  if (sectorMatches(sec, 'real estate', 'reit', 'utilit')) {
    if (rates === 'up') return { tone: 'headwind', satz: 'Makro-Gegenwind: steigende Zinsen belasten zinssensible Werte (REITs/Versorger).' };
    if (rates === 'down') return { tone: 'tailwind', satz: 'Makro-Rückenwind: fallende Zinsen entlasten zinssensible Werte.' };
  }

  // Banken/Finanz: profitieren von steigenden Zinsen
  if (sectorMatches(sec, 'financ', 'bank', 'insurance')) {
    if (rates === 'up') return { tone: 'tailwind', satz: 'Makro-Rückenwind: steigende Zinsen stützen die Zinsmarge von Banken/Finanz.' };
    if (rates === 'down') return { tone: 'headwind', satz: 'Makro-Gegenwind: fallende Zinsen drücken die Zinsmarge.' };
  }

  // Wachstum/Tech/Tesla/Theme — Dollar- & zinssensibel (Mega-Cap-Export, lange Duration)
  if (sub === 'momentum' || sub === 'tesla' || sub === 'theme') {
    if (rates === 'up' && dollar === 'up')
      return { tone: 'headwind', satz: 'Makro-Gegenwind: steigende Zinsen UND starker Dollar bremsen Wachstums-/Tech-Werte.' };
    if (rates === 'down')
      return { tone: 'tailwind', satz: 'Makro-Rückenwind: fallende Zinsen begünstigen Wachstums-/Tech-Bewertungen.' };
  }

  // Sonst: an der Gesamtlage orientieren (schwaches Signal)
  if (regime.tone === 'risk_off')
    return { tone: 'headwind', satz: 'Leichter Makro-Gegenwind aus der Risk-off-Gesamtlage.' };
  if (regime.tone === 'risk_on')
    return { tone: 'tailwind', satz: 'Leichter Makro-Rückenwind aus der Risk-on-Gesamtlage.' };
  return { tone: 'neutral', satz: 'Kein klarer Makro-Einfluss auf diesen Wert.' };
}
